import type { FC } from "react";
import { useNavigate } from "react-router-dom";

type HomePageProps = {
  allowDevelopUse: boolean;
};

type FeatureCard = {
  to: string;
  label: string;
  description: string;
  icon: JSX.Element;
};

const SearchIcon = () => (
  <svg
    className="w-8 h-8"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
);

const SendIcon = () => (
  <svg
    className="w-8 h-8"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
    />
  </svg>
);

const CalendarIcon = () => (
  <svg
    className="w-8 h-8"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

const HistoryIcon = () => (
  <svg
    className="w-8 h-8"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const GroupIcon = () => (
  <svg
    className="w-8 h-8"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
    />
  </svg>
);

const NetworkBackground = () => (
  <svg
    className="w-full h-full"
    viewBox="0 0 1000 1000"
    preserveAspectRatio="xMidYMid slice"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* Dense network with varied opacity - mostly invisible with occasional visible spots */}
    {/* Horizontal lines - Row 1 */}
    <line
      x1="0"
      y1="50"
      x2="100"
      y2="70"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.04"
    />
    <line
      x1="100"
      y1="70"
      x2="220"
      y2="40"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.45"
    />
    <line
      x1="220"
      y1="40"
      x2="350"
      y2="80"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.02"
    />
    <line
      x1="350"
      y1="80"
      x2="480"
      y2="55"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.06"
    />
    <line
      x1="480"
      y1="55"
      x2="600"
      y2="90"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.01"
    />
    <line
      x1="600"
      y1="90"
      x2="720"
      y2="45"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.25"
    />
    <line
      x1="720"
      y1="45"
      x2="850"
      y2="75"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.03"
    />
    <line
      x1="850"
      y1="75"
      x2="1000"
      y2="60"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.1"
    />

    {/* Row 2 */}
    <line
      x1="0"
      y1="180"
      x2="130"
      y2="160"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.08"
    />
    <line
      x1="130"
      y1="160"
      x2="260"
      y2="200"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.02"
    />
    <line
      x1="260"
      y1="200"
      x2="400"
      y2="170"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.55"
    />
    <line
      x1="400"
      y1="170"
      x2="530"
      y2="190"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.03"
    />
    <line
      x1="530"
      y1="190"
      x2="670"
      y2="155"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.01"
    />
    <line
      x1="670"
      y1="155"
      x2="800"
      y2="185"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.35"
    />
    <line
      x1="800"
      y1="185"
      x2="920"
      y2="165"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.01"
    />
    <line
      x1="920"
      y1="165"
      x2="1000"
      y2="175"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.06"
    />

    {/* Row 3 */}
    <line
      x1="0"
      y1="300"
      x2="150"
      y2="320"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.03"
    />
    <line
      x1="150"
      y1="320"
      x2="280"
      y2="285"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.1"
    />
    <line
      x1="280"
      y1="285"
      x2="420"
      y2="310"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.01"
    />
    <line
      x1="420"
      y1="310"
      x2="550"
      y2="290"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.04"
    />
    <line
      x1="550"
      y1="290"
      x2="700"
      y2="325"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.48"
    />
    <line
      x1="700"
      y1="325"
      x2="830"
      y2="295"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.02"
    />
    <line
      x1="830"
      y1="295"
      x2="950"
      y2="315"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.08"
    />
    <line
      x1="950"
      y1="315"
      x2="1000"
      y2="305"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.03"
    />

    {/* Row 4 */}
    <line
      x1="0"
      y1="420"
      x2="120"
      y2="400"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.28"
    />
    <line
      x1="120"
      y1="400"
      x2="250"
      y2="435"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.01"
    />
    <line
      x1="250"
      y1="435"
      x2="380"
      y2="410"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.06"
    />
    <line
      x1="380"
      y1="410"
      x2="520"
      y2="440"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.02"
    />
    <line
      x1="520"
      y1="440"
      x2="650"
      y2="405"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.01"
    />
    <line
      x1="650"
      y1="405"
      x2="780"
      y2="430"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.52"
    />
    <line
      x1="780"
      y1="430"
      x2="900"
      y2="415"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.03"
    />
    <line
      x1="900"
      y1="415"
      x2="1000"
      y2="425"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.02"
    />

    {/* Row 5 */}
    <line
      x1="0"
      y1="540"
      x2="140"
      y2="560"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.02"
    />
    <line
      x1="140"
      y1="560"
      x2="270"
      y2="530"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.08"
    />
    <line
      x1="270"
      y1="530"
      x2="410"
      y2="555"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.01"
    />
    <line
      x1="410"
      y1="555"
      x2="540"
      y2="525"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.32"
    />
    <line
      x1="540"
      y1="525"
      x2="680"
      y2="565"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.02"
    />
    <line
      x1="680"
      y1="565"
      x2="810"
      y2="540"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.04"
    />
    <line
      x1="810"
      y1="540"
      x2="940"
      y2="570"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.4"
    />
    <line
      x1="940"
      y1="570"
      x2="1000"
      y2="550"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.01"
    />

    {/* Row 6 */}
    <line
      x1="0"
      y1="660"
      x2="110"
      y2="680"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.06"
    />
    <line
      x1="110"
      y1="680"
      x2="240"
      y2="650"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.01"
    />
    <line
      x1="240"
      y1="650"
      x2="370"
      y2="690"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.12"
    />
    <line
      x1="370"
      y1="690"
      x2="500"
      y2="665"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.02"
    />
    <line
      x1="500"
      y1="665"
      x2="640"
      y2="695"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.01"
    />
    <line
      x1="640"
      y1="695"
      x2="770"
      y2="655"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.48"
    />
    <line
      x1="770"
      y1="655"
      x2="890"
      y2="685"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.02"
    />
    <line
      x1="890"
      y1="685"
      x2="1000"
      y2="670"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.08"
    />

    {/* Row 7 */}
    <line
      x1="0"
      y1="780"
      x2="160"
      y2="800"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.03"
    />
    <line
      x1="160"
      y1="800"
      x2="290"
      y2="770"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.35"
    />
    <line
      x1="290"
      y1="770"
      x2="430"
      y2="810"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.01"
    />
    <line
      x1="430"
      y1="810"
      x2="560"
      y2="785"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.04"
    />
    <line
      x1="560"
      y1="785"
      x2="700"
      y2="815"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.02"
    />
    <line
      x1="700"
      y1="815"
      x2="830"
      y2="775"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.06"
    />
    <line
      x1="830"
      y1="775"
      x2="960"
      y2="795"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.55"
    />
    <line
      x1="960"
      y1="795"
      x2="1000"
      y2="790"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.03"
    />

    {/* Row 8 */}
    <line
      x1="0"
      y1="900"
      x2="130"
      y2="920"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.12"
    />
    <line
      x1="130"
      y1="920"
      x2="260"
      y2="890"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.01"
    />
    <line
      x1="260"
      y1="890"
      x2="400"
      y2="930"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.04"
    />
    <line
      x1="400"
      y1="930"
      x2="530"
      y2="905"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.5"
    />
    <line
      x1="530"
      y1="905"
      x2="670"
      y2="940"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.02"
    />
    <line
      x1="670"
      y1="940"
      x2="800"
      y2="910"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.06"
    />
    <line
      x1="800"
      y1="910"
      x2="920"
      y2="935"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.01"
    />
    <line
      x1="920"
      y1="935"
      x2="1000"
      y2="915"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.28"
    />

    {/* Vertical connections */}
    <line
      x1="100"
      y1="70"
      x2="130"
      y2="160"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.03"
    />
    <line
      x1="220"
      y1="40"
      x2="260"
      y2="200"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.08"
    />
    <line
      x1="350"
      y1="80"
      x2="400"
      y2="170"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.01"
    />
    <line
      x1="480"
      y1="55"
      x2="530"
      y2="190"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.4"
    />
    <line
      x1="600"
      y1="90"
      x2="670"
      y2="155"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.02"
    />
    <line
      x1="720"
      y1="45"
      x2="800"
      y2="185"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.04"
    />
    <line
      x1="850"
      y1="75"
      x2="920"
      y2="165"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.28"
    />

    <line
      x1="130"
      y1="160"
      x2="150"
      y2="320"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.01"
    />
    <line
      x1="260"
      y1="200"
      x2="280"
      y2="285"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.32"
    />
    <line
      x1="400"
      y1="170"
      x2="420"
      y2="310"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.02"
    />
    <line
      x1="530"
      y1="190"
      x2="550"
      y2="290"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.04"
    />
    <line
      x1="670"
      y1="155"
      x2="700"
      y2="325"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.01"
    />
    <line
      x1="800"
      y1="185"
      x2="830"
      y2="295"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.5"
    />
    <line
      x1="920"
      y1="165"
      x2="950"
      y2="315"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.02"
    />

    <line
      x1="150"
      y1="320"
      x2="120"
      y2="400"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.06"
    />
    <line
      x1="280"
      y1="285"
      x2="250"
      y2="435"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.01"
    />
    <line
      x1="420"
      y1="310"
      x2="380"
      y2="410"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.22"
    />
    <line
      x1="550"
      y1="290"
      x2="520"
      y2="440"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.01"
    />
    <line
      x1="700"
      y1="325"
      x2="650"
      y2="405"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.04"
    />
    <line
      x1="830"
      y1="295"
      x2="780"
      y2="430"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.02"
    />
    <line
      x1="950"
      y1="315"
      x2="900"
      y2="415"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.35"
    />

    <line
      x1="120"
      y1="400"
      x2="140"
      y2="560"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.01"
    />
    <line
      x1="250"
      y1="435"
      x2="270"
      y2="530"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.04"
    />
    <line
      x1="380"
      y1="410"
      x2="410"
      y2="555"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.48"
    />
    <line
      x1="520"
      y1="440"
      x2="540"
      y2="525"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.02"
    />
    <line
      x1="650"
      y1="405"
      x2="680"
      y2="565"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.06"
    />
    <line
      x1="780"
      y1="430"
      x2="810"
      y2="540"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.01"
    />
    <line
      x1="900"
      y1="415"
      x2="940"
      y2="570"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.03"
    />

    <line
      x1="140"
      y1="560"
      x2="110"
      y2="680"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.08"
    />
    <line
      x1="270"
      y1="530"
      x2="240"
      y2="650"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.01"
    />
    <line
      x1="410"
      y1="555"
      x2="370"
      y2="690"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.03"
    />
    <line
      x1="540"
      y1="525"
      x2="500"
      y2="665"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.32"
    />
    <line
      x1="680"
      y1="565"
      x2="640"
      y2="695"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.01"
    />
    <line
      x1="810"
      y1="540"
      x2="770"
      y2="655"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.04"
    />
    <line
      x1="940"
      y1="570"
      x2="890"
      y2="685"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.52"
    />

    <line
      x1="110"
      y1="680"
      x2="160"
      y2="800"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.01"
    />
    <line
      x1="240"
      y1="650"
      x2="290"
      y2="770"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.22"
    />
    <line
      x1="370"
      y1="690"
      x2="430"
      y2="810"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.02"
    />
    <line
      x1="500"
      y1="665"
      x2="560"
      y2="785"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.04"
    />
    <line
      x1="640"
      y1="695"
      x2="700"
      y2="815"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.4"
    />
    <line
      x1="770"
      y1="655"
      x2="830"
      y2="775"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.01"
    />
    <line
      x1="890"
      y1="685"
      x2="960"
      y2="795"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.06"
    />

    <line
      x1="160"
      y1="800"
      x2="130"
      y2="920"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.03"
    />
    <line
      x1="290"
      y1="770"
      x2="260"
      y2="890"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.01"
    />
    <line
      x1="430"
      y1="810"
      x2="400"
      y2="930"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.28"
    />
    <line
      x1="560"
      y1="785"
      x2="530"
      y2="905"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.01"
    />
    <line
      x1="700"
      y1="815"
      x2="670"
      y2="940"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.04"
    />
    <line
      x1="830"
      y1="775"
      x2="800"
      y2="910"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.35"
    />
    <line
      x1="960"
      y1="795"
      x2="920"
      y2="935"
      stroke="#d1d5db"
      strokeWidth="0.4"
      opacity="0.02"
    />

    {/* Circles on top - varied opacity for appearing/disappearing effect */}
    {/* Row 1 */}
    <circle cx="0" cy="50" r="1.2" fill="#d1d5db" opacity="0.06" />
    <circle cx="100" cy="70" r="1.5" fill="#d1d5db" opacity="0.02" />
    <circle cx="220" cy="40" r="1.2" fill="#d1d5db" opacity="0.48" />
    <circle cx="350" cy="80" r="1.5" fill="#d1d5db" opacity="0.01" />
    <circle cx="480" cy="55" r="1.2" fill="#d1d5db" opacity="0.08" />
    <circle cx="600" cy="90" r="1.5" fill="#d1d5db" opacity="0.02" />
    <circle cx="720" cy="45" r="1.2" fill="#d1d5db" opacity="0.3" />
    <circle cx="850" cy="75" r="1.5" fill="#d1d5db" opacity="0.03" />
    <circle cx="1000" cy="60" r="1.2" fill="#d1d5db" opacity="0.1" />

    {/* Row 2 */}
    <circle cx="0" cy="180" r="1.5" fill="#d1d5db" opacity="0.04" />
    <circle cx="130" cy="160" r="1.2" fill="#d1d5db" opacity="0.1" />
    <circle cx="260" cy="200" r="1.5" fill="#d1d5db" opacity="0.01" />
    <circle cx="400" cy="170" r="1.2" fill="#d1d5db" opacity="0.6" />
    <circle cx="530" cy="190" r="1.5" fill="#d1d5db" opacity="0.03" />
    <circle cx="670" cy="155" r="1.2" fill="#d1d5db" opacity="0.04" />
    <circle cx="800" cy="185" r="1.5" fill="#d1d5db" opacity="0.4" />
    <circle cx="920" cy="165" r="1.2" fill="#d1d5db" opacity="0.01" />
    <circle cx="1000" cy="175" r="1.5" fill="#d1d5db" opacity="0.06" />

    {/* Row 3 */}
    <circle cx="0" cy="300" r="1.2" fill="#d1d5db" opacity="0.02" />
    <circle cx="150" cy="320" r="1.5" fill="#d1d5db" opacity="0.04" />
    <circle cx="280" cy="285" r="1.2" fill="#d1d5db" opacity="0.28" />
    <circle cx="420" cy="310" r="1.5" fill="#d1d5db" opacity="0.01" />
    <circle cx="550" cy="290" r="1.2" fill="#d1d5db" opacity="0.03" />
    <circle cx="700" cy="325" r="1.5" fill="#d1d5db" opacity="0.52" />
    <circle cx="830" cy="295" r="1.2" fill="#d1d5db" opacity="0.02" />
    <circle cx="950" cy="315" r="1.5" fill="#d1d5db" opacity="0.08" />
    <circle cx="1000" cy="305" r="1.2" fill="#d1d5db" opacity="0.01" />

    {/* Row 4 */}
    <circle cx="0" cy="420" r="1.5" fill="#d1d5db" opacity="0.22" />
    <circle cx="120" cy="400" r="1.2" fill="#d1d5db" opacity="0.32" />
    <circle cx="250" cy="435" r="1.5" fill="#d1d5db" opacity="0.01" />
    <circle cx="380" cy="410" r="1.2" fill="#d1d5db" opacity="0.06" />
    <circle cx="520" cy="440" r="1.5" fill="#d1d5db" opacity="0.02" />
    <circle cx="650" cy="405" r="1.2" fill="#d1d5db" opacity="0.03" />
    <circle cx="780" cy="430" r="1.5" fill="#d1d5db" opacity="0.55" />
    <circle cx="900" cy="415" r="1.2" fill="#d1d5db" opacity="0.04" />
    <circle cx="1000" cy="425" r="1.5" fill="#d1d5db" opacity="0.01" />

    {/* Row 5 */}
    <circle cx="0" cy="540" r="1.2" fill="#d1d5db" opacity="0.03" />
    <circle cx="140" cy="560" r="1.5" fill="#d1d5db" opacity="0.01" />
    <circle cx="270" cy="530" r="1.2" fill="#d1d5db" opacity="0.1" />
    <circle cx="410" cy="555" r="1.5" fill="#d1d5db" opacity="0.02" />
    <circle cx="540" cy="525" r="1.2" fill="#d1d5db" opacity="0.35" />
    <circle cx="680" cy="565" r="1.5" fill="#d1d5db" opacity="0.01" />
    <circle cx="810" cy="540" r="1.2" fill="#d1d5db" opacity="0.04" />
    <circle cx="940" cy="570" r="1.5" fill="#d1d5db" opacity="0.45" />
    <circle cx="1000" cy="550" r="1.2" fill="#d1d5db" opacity="0.02" />

    {/* Row 6 */}
    <circle cx="0" cy="660" r="1.5" fill="#d1d5db" opacity="0.06" />
    <circle cx="110" cy="680" r="1.2" fill="#d1d5db" opacity="0.08" />
    <circle cx="240" cy="650" r="1.5" fill="#d1d5db" opacity="0.01" />
    <circle cx="370" cy="690" r="1.2" fill="#d1d5db" opacity="0.28" />
    <circle cx="500" cy="665" r="1.5" fill="#d1d5db" opacity="0.03" />
    <circle cx="640" cy="695" r="1.2" fill="#d1d5db" opacity="0.02" />
    <circle cx="770" cy="655" r="1.5" fill="#d1d5db" opacity="0.5" />
    <circle cx="890" cy="685" r="1.2" fill="#d1d5db" opacity="0.01" />
    <circle cx="1000" cy="670" r="1.5" fill="#d1d5db" opacity="0.06" />

    {/* Row 7 */}
    <circle cx="0" cy="780" r="1.2" fill="#d1d5db" opacity="0.01" />
    <circle cx="160" cy="800" r="1.5" fill="#d1d5db" opacity="0.04" />
    <circle cx="290" cy="770" r="1.2" fill="#d1d5db" opacity="0.4" />
    <circle cx="430" cy="810" r="1.5" fill="#d1d5db" opacity="0.02" />
    <circle cx="560" cy="785" r="1.2" fill="#d1d5db" opacity="0.03" />
    <circle cx="700" cy="815" r="1.5" fill="#d1d5db" opacity="0.01" />
    <circle cx="830" cy="775" r="1.2" fill="#d1d5db" opacity="0.08" />
    <circle cx="960" cy="795" r="1.5" fill="#d1d5db" opacity="0.6" />
    <circle cx="1000" cy="790" r="1.2" fill="#d1d5db" opacity="0.03" />

    {/* Row 8 */}
    <circle cx="0" cy="900" r="1.5" fill="#d1d5db" opacity="0.08" />
    <circle cx="130" cy="920" r="1.2" fill="#d1d5db" opacity="0.28" />
    <circle cx="260" cy="890" r="1.5" fill="#d1d5db" opacity="0.01" />
    <circle cx="400" cy="930" r="1.2" fill="#d1d5db" opacity="0.03" />
    <circle cx="530" cy="905" r="1.5" fill="#d1d5db" opacity="0.52" />
    <circle cx="670" cy="940" r="1.2" fill="#d1d5db" opacity="0.02" />
    <circle cx="800" cy="910" r="1.5" fill="#d1d5db" opacity="0.06" />
    <circle cx="920" cy="935" r="1.2" fill="#d1d5db" opacity="0.01" />
    <circle cx="1000" cy="915" r="1.5" fill="#d1d5db" opacity="0.32" />
  </svg>
);

export const HomePage: FC<HomePageProps> = ({ allowDevelopUse }) => {
  const navigate = useNavigate();

  const featureCards: FeatureCard[] = [
    {
      to: "/emails/search",
      label: "メール検索",
      description: "AIで最適な人材をマッチング",
      icon: <SearchIcon />,
    },
    ...(allowDevelopUse
      ? [
          {
            to: "/emails/bulk",
            label: "一斉配信",
            description: "効率的な大量メール送信",
            icon: <SendIcon />,
          },
          {
            to: "/emails/schedules",
            label: "予約一覧",
            description: "配信スケジュールを管理",
            icon: <CalendarIcon />,
          },
          {
            to: "/emails/histories",
            label: "配信履歴",
            description: "過去の配信を確認",
            icon: <HistoryIcon />,
          },
          {
            to: "/admin/distribution/groups",
            label: "配信グループ",
            description: "連絡先グループを管理",
            icon: <GroupIcon />,
          },
        ]
      : []),
  ];

  return (
    <div className="min-h-[calc(100vh-64px)] flex flex-col items-center justify-center px-4 relative">
      {/* Network Background - screen right-bottom edge */}
      <div className="fixed top-0 right-0 w-screen h-screen pointer-events-none z-[1]">
        <NetworkBackground />
      </div>

      {/* Hero Section */}
      <div className="text-center mb-12 relative z-10">
        {/* Logo */}
        <div className="mb-8">
          <img
            className="w-full max-w-2xl h-auto mx-auto drop-shadow-lg"
            src={`${process.env.PUBLIC_URL}/images/strix_search_top_logo1.png`}
            alt="Strix Search"
          />
        </div>

        {/* Tagline */}
        <p className="text-2xl md:text-3xl font-light text-gray-700 tracking-wide">
          マッチング業務を、もっと
          <span className="text-primary font-semibold">スマート</span>に
        </p>
        <p className="mt-4 text-gray-500 text-lg">
          AIを活用したメール検索で、最適な人材マッチングを実現
        </p>
      </div>

      {/* Feature Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl w-full relative z-10">
        {featureCards.map((card, index) => (
          <button
            type="button"
            key={card.to}
            onClick={() => navigate(card.to)}
            className="group bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100 hover:border-primary/30 text-left"
            style={{
              animationDelay: `${index * 100}ms`,
            }}
          >
            {/* Icon */}
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors duration-300 mb-4">
              {card.icon}
            </div>

            {/* Content */}
            <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-primary transition-colors">
              {card.label}
            </h3>
            <p className="text-gray-500 text-sm">{card.description}</p>

            {/* Arrow */}
            <div className="mt-4 flex items-center text-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <span className="text-sm font-medium">開く</span>
              <svg
                className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          </button>
        ))}
      </div>

      {/* Footer decoration */}
      <div className="mt-16 text-center text-gray-400 text-sm relative z-10">
        <p>Powered by AI Technology</p>
      </div>
    </div>
  );
};
